import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { useFeather } from '../shell/useFeather';
import {
  listMailTemplates, getMailTemplate, createMailTemplate, updateMailTemplate,
  archiveMailTemplate, restoreMailTemplate, getMailerStatus, sendMailerTest,
  type MailTemplateListItem, type MailTemplateDetail, type MailerStatus,
} from '../api/crm';
import { confirmDialog, noticeDialog } from '../shell/dialogs';

const LANGS: [string, string][] = [['cs', '🇨🇿 CS'], ['sk', '🇸🇰 SK'], ['ro', '🇷🇴 RO'], ['en', '🇬🇧 EN']];

const PREVIEW_VARS: Record<string, string> = {
  FIRST_NAME: 'Jan', LAST_NAME: 'Novák', NAME: 'Jan Novák', COMPANY: 'Firma s.r.o.',
  EMAIL: 'jan.novak@firma.cz', BRAND: 'LuckyBet',
  CONFIRM_URL: 'https://affilplatform.platform1.cz/potvrdit/demo-token',
  PRIVACY_URL: 'https://affilplatform.platform1.cz/soukromi',
  VOCATIVE: 'Jane', DOCUMENT_TITLE: 'Smlouva o spolupráci', SENDER_NAME: 'Petr Svoboda',
  SIGN_URL: 'https://affilmaster.platform1.cz/podpis/demo', SIGNED_COUNT: '1', SIGNERS_TOTAL: '2',
};

const DEFAULT_VARIABLES = ['FIRST_NAME', 'CONFIRM_URL', 'PRIVACY_URL', 'COMPANY', 'BRAND'];
const ESIGN_VARIABLES = ['VOCATIVE', 'DOCUMENT_TITLE', 'SENDER_NAME', 'SIGN_URL', 'SIGNED_COUNT', 'SIGNERS_TOTAL'];

function variablesFor(name: string | null | undefined): string[] {
  return (name ?? '').toLowerCase().startsWith('esign') ? ESIGN_VARIABLES : DEFAULT_VARIABLES;
}
function renderVars(s: string | null | undefined): string {
  if (!s) return '';
  return String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, k) => PREVIEW_VARS[k] ?? `{{${k}}}`);
}
function fmtDate(s: string): string {
  if (!s) return '-';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('cs-CZ');
}

export function CrmServisniSablonyPage() {
  const [editId, setEditId] = useState<number | 'new' | null>(null);
  if (editId != null) {
    return <TemplateEditor id={editId === 'new' ? null : editId} onBack={() => setEditId(null)} />;
  }
  return <Library onEdit={setEditId} onNew={() => setEditId('new')} />;
}

function MailPreview({ subject, html }: { subject: string; html: string }) {
  return (
    <div className="settings-mail-preview">
      <div className="settings-mail-preview-subject">
        <span>Předmět</span>
        <strong>{renderVars(subject) || 'Bez předmětu'}</strong>
      </div>
      <iframe title="Náhled e-mailu" sandbox="allow-same-origin" srcDoc={renderVars(html)} />
    </div>
  );
}

function MailerStatusPanel() {
  const [st, setSt] = useState<MailerStatus | null>(null);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => { getMailerStatus().then(setSt).catch(() => setSt(null)); }, []);

  async function sendTest() {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())) { setMsg({ ok: false, text: 'Zadej platný e-mail.' }); return; }
    setBusy(true); setMsg(null);
    try { await sendMailerTest(to.trim()); setMsg({ ok: true, text: 'Odesláno ✓' }); }
    catch (e) { setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Odeslání selhalo' }); }
    finally { setBusy(false); }
  }

  const on = st?.configured;
  return (
    <section className={`settings-mail-status${on ? ' is-connected' : ' is-offline'}`} aria-label="Stav odesílatele">
      <div className="settings-mail-status-copy">
        <span className="settings-mail-status-dot" aria-hidden="true" />
        <div>
          <strong>{st?.provider ?? 'SendGrid'}</strong>
          <small>{st?.fromName || st?.fromEmail ? `${st?.fromName ?? ''} <${st?.fromEmail ?? '-'}>` : 'Odesílatel zatím není nakonfigurovaný'}</small>
        </div>
        <span className="settings-mail-status-pill">{on ? 'Napojeno' : 'Nenapojeno'}</span>
      </div>
      <div className="settings-mail-test">
        <label htmlFor="settings-mail-test-address">Test doručení</label>
        <input
          id="settings-mail-test-address"
          className="form-control"
          type="email"
          placeholder="test@prijemce.cz"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          disabled={!on || busy}
        />
        <button className="btn btn-outline-secondary" onClick={sendTest} disabled={!on || busy}>
          {busy ? 'Odesílám…' : 'Poslat test'}
        </button>
      </div>
      {msg && <div className={`settings-mail-status-message ${msg.ok ? 'is-ok' : 'is-error'}`}>{msg.text}</div>}
    </section>
  );
}

function Library({ onEdit, onNew }: { onEdit: (id: number) => void; onNew: () => void }) {
  const [list, setList] = useState<MailTemplateListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [preview, setPreview] = useState<MailTemplateDetail | null>(null);

  function reload() {
    setList(null);
    listMailTemplates(showArchived)
      .then(setList)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }
  useEffect(reload, [showArchived]);
  useFeather([list]);

  async function openPreview(t: MailTemplateListItem) {
    try { setPreview(await getMailTemplate(t.id)); } catch { /* ignore */ }
  }
  async function onArchive(t: MailTemplateListItem) {
    if (!(await confirmDialog({ title: 'Archivovat šablonu?', confirmLabel: 'Archivovat' }))) return;
    await archiveMailTemplate(t.id); reload();
  }
  async function onRestore(t: MailTemplateListItem) { await restoreMailTemplate(t.id); reload(); }

  return (
    <div className="settings-template-library">
      <MailerStatusPanel />

      <div className="settings-template-toolbar" aria-label="Nástroje šablon">
        <div className="settings-template-filter">
          <label htmlFor="settings-template-status">Zobrazení</label>
          <select id="settings-template-status" className="form-select" value={showArchived ? '1' : '0'} onChange={(e) => setShowArchived(e.target.value === '1')}>
            <option value="0">Aktivní šablony</option>
            <option value="1">Včetně archivovaných</option>
          </select>
        </div>
        <span className="settings-template-count">{list?.length ?? 0} šablon</span>
        <button type="button" className="btn btn-primary" onClick={onNew}>
          <span className="feather-icon" aria-hidden="true"><i data-feather="plus"></i></span>
          Nová šablona
        </button>
      </div>

      {error ? <div className="alert alert-danger">{error}</div>
        : !list ? <div className="settings-template-loading">Načítám šablony…</div>
        : list.length === 0 ? (
          <section className="settings-template-empty">
            <span className="settings-template-empty-icon" aria-hidden="true"><span className="feather-icon"><i data-feather="mail"></i></span></span>
            <h2>První servisní šablona</h2>
            <p>Připravte obsah potvrzení registrace nebo jiného transakčního e-mailu.</p>
            <button className="btn btn-primary" onClick={onNew}>Vytvořit šablonu</button>
          </section>
        ) : (
          <div className="settings-template-table-wrap">
            <table className="table align-middle settings-template-table">
              <thead>
                <tr>
                  <th>Název</th>
                  <th>Předmět</th>
                  <th>Jazyk</th>
                  <th>Vytvořeno</th>
                  <th>Akce</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className={t.enabled ? '' : 'is-archived'}>
                    <td>
                      <div className="settings-template-name">
                        <strong>{t.name}</strong>
                        {!t.enabled && <span>Archiv</span>}
                      </div>
                      {t.purpose && <small>{t.purpose}</small>}
                    </td>
                    <td>{t.subject}</td>
                    <td><span className="settings-language-chip">{LANGS.find((l) => l[0] === t.lang)?.[1] ?? t.lang}</span></td>
                    <td>{fmtDate(t.createdAt)}</td>
                    <td>
                      <div className="settings-template-actions">
                        <button className="btn" aria-label={`Náhled ${t.name}`} title="Náhled" onClick={() => openPreview(t)}><span className="feather-icon"><i data-feather="eye"></i></span></button>
                        <button className="btn" aria-label={`Upravit ${t.name}`} title="Upravit" onClick={() => onEdit(t.id)}><span className="feather-icon"><i data-feather="edit-2"></i></span></button>
                        {t.enabled
                          ? <button className="btn is-danger" aria-label={`Archivovat ${t.name}`} title="Archivovat" onClick={() => onArchive(t)}><span className="feather-icon"><i data-feather="archive"></i></span></button>
                          : <button className="btn is-success" aria-label={`Obnovit ${t.name}`} title="Obnovit" onClick={() => onRestore(t)}><span className="feather-icon"><i data-feather="rotate-ccw"></i></span></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {preview && (
        <div className="settings-template-preview-layer" role="dialog" aria-modal="true" aria-labelledby="settings-template-preview-title" onClick={() => setPreview(null)}>
          <div className="settings-template-preview-dialog" onClick={(e) => e.stopPropagation()}>
            <header>
              <div>
                <span>Náhled šablony</span>
                <h2 id="settings-template-preview-title">{preview.name}</h2>
              </div>
              <button className="btn-close" aria-label="Zavřít" onClick={() => setPreview(null)} />
            </header>
            <div className="settings-template-preview-body"><MailPreview subject={preview.subject} html={preview.bodyHtml ?? ''} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ id, onBack }: { id: number | null; onBack: () => void }) {
  const [data, setData] = useState<Partial<MailTemplateDetail>>({ lang: 'cs' });
  const [loading, setLoading] = useState(id != null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pk, setPk] = useState(0);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useFeather([id, pk, saved]);

  useEffect(() => {
    if (id == null) { setData({ lang: 'cs' }); return; }
    getMailTemplate(id).then(setData).catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Načtení selhalo')).finally(() => setLoading(false));
  }, [id]);

  function set<K extends keyof MailTemplateDetail>(k: K, v: MailTemplateDetail[K]) { setData((d) => ({ ...d, [k]: v })); setSaved(false); }

  async function save() {
    if (!data.name || !data.name.trim()) { void noticeDialog({ title: 'Chybí název', message: 'Vyplň název šablony.' }); return; }
    if (!data.subject || !data.subject.trim()) { void noticeDialog({ title: 'Chybí předmět', message: 'Vyplň předmět e-mailu.' }); return; }
    setSaving(true); setErr(null);
    try {
      const body = { name: data.name, lang: data.lang ?? 'cs', subject: data.subject, purpose: data.purpose ?? null, bodyHtml: data.bodyHtml ?? null, bodyPlain: data.bodyPlain ?? null };
      if (id == null) { const r = await createMailTemplate(body); const d = await getMailTemplate(r.id); setData(d); setLoading(false); }
      else { await updateMailTemplate(id, body); }
      setSaved(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 1500);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Uložení selhalo'); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="text-muted py-4">Načítám…</div>;

  return (
    <div className="settings-template-editor">
      <header className="settings-template-editor-head">
        <div>
          <button type="button" className="settings-template-back" onClick={onBack}>
            <span className="feather-icon" aria-hidden="true"><i data-feather="arrow-left"></i></span>
            Šablony
          </button>
          <span>{id == null ? 'Nová šablona' : 'Editace šablony'}</span>
          <h2>{id == null ? 'Připravit servisní e-mail' : (data.name || 'Servisní e-mail')}</h2>
          <p>Obsah, který zákazník dostane při systémové události.</p>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saved ? 'Uloženo' : saving ? 'Ukládám…' : 'Uložit šablonu'}
        </button>
      </header>
      {err && <div className="alert alert-danger">{err}</div>}

      <div className="settings-template-editor-grid">
        <section className="settings-template-editor-form">
          <header>
            <span>Obsah</span>
            <h3>Základní údaje a zpráva</h3>
          </header>
          <div className="settings-template-fields">
            <div className="settings-template-field-grid">
              <div className="settings-field">
                <label className="form-label">Název pro tým <span className="text-danger">*</span></label>
                <input className="form-control" value={data.name ?? ''} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div className="settings-field">
                <label className="form-label">Jazyk</label>
                <select className="form-select" value={data.lang ?? 'cs'} onChange={(e) => set('lang', e.target.value)}>
                  {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="settings-field">
              <label className="form-label">Předmět e-mailu <span className="text-danger">*</span></label>
              <input className="form-control" value={data.subject ?? ''} onChange={(e) => set('subject', e.target.value)} />
            </div>
            <div className="settings-field">
              <label className="form-label">Interní účel</label>
              <input className="form-control" placeholder="Např. potvrzení registrace" value={data.purpose ?? ''} onChange={(e) => set('purpose', e.target.value)} />
            </div>
            <div className="settings-field">
              <label className="form-label">HTML obsah</label>
              <textarea className="form-control settings-template-code" rows={16} value={data.bodyHtml ?? ''} onChange={(e) => set('bodyHtml', e.target.value)} />
              <div className="settings-template-variables">
                <span>Proměnné</span>
                {variablesFor(data.name).map((variable) => <code key={variable}>{`{{${variable}}}`}</code>)}
              </div>
            </div>
            <div className="settings-field">
              <label className="form-label">Textová verze</label>
              <textarea className="form-control" rows={5} value={data.bodyPlain ?? ''} onChange={(e) => set('bodyPlain', e.target.value)} />
              <small>Použije se v poštovních klientech bez HTML.</small>
            </div>
          </div>
        </section>

        <aside className="settings-template-editor-preview">
          <header>
            <div>
              <span>Živý náhled</span>
              <h3>E-mail příjemce</h3>
            </div>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setPk((k) => k + 1)}>
              <span className="feather-icon" aria-hidden="true"><i data-feather="refresh-cw"></i></span>
              Obnovit
            </button>
          </header>
          <MailPreview key={pk} subject={data.subject ?? ''} html={data.bodyHtml ?? ''} />
        </aside>
      </div>
    </div>
  );
}
